// src/App.js
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import { CustomerProvider } from './context/CustomerContext';
import { RequireSupportSignIn } from './components/support/supportUi';
import OtpGate from './components/OtpGate';
import PatchNotesModal from './components/PatchNotesModal';
import Glyph from './components/Glyph';
import './index.css';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const DailyCoursePage = lazy(() => import('./pages/DailyCoursePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const ProfileSetup = lazy(() => import('./pages/ProfileSetup'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ReportActivityPage = lazy(() => import('./pages/ReportActivityPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const SupportChatPage = lazy(() => import('./pages/SupportChatPage'));
const SupportTicketPage = lazy(() => import('./pages/SupportTicketPage'));
const MyTicketsPage = lazy(() => import('./pages/MyTicketsPage'));

// Single authoritative deadline for how long we wait on Firebase auth before
// falling back to guest mode. Progress % is derived from elapsed time against
// this same number, so there's only one clock driving both the bar and the
// cutoff — no separate decorative animation that can finish (or stall)
// independently of the real wait.
const AUTH_TIMEOUT_MS = 9000;
const STALLED_AT_MS = 6000;
// Absolute backstop. Even if the logic above regresses again, nothing should
// ever keep this loader on screen past this ceiling.
const HARD_CEILING_MS = 12000;

// No guest browsing anywhere under /app or onboarding — anyone without a
// live session is bounced straight to the homepage, not even to /login.
// The homepage's own "Get started" flow is the only door in.
function RequireAuth({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  if (!currentUser) return <Navigate to="/" replace />;
  return children;
}

function Loader({ pct, stalled }) {
  const rounded = Math.round(pct);
  return (
    <div className={`lp-loader${rounded >= 100 ? ' done' : ''}`}>
      <Glyph size={88} mode="drop" />
      <div className="lp-loader-wordmark">elpis</div>
      <div className="lp-loader-pct">{String(rounded).padStart(2, '0')}%</div>
      <div className="lp-loader-track">
        <div className="lp-loader-bar" style={{ width: `${rounded}%` }} />
      </div>
      <div className="lp-loader-label">
        {stalled
          ? 'TAKING LONGER THAN EXPECTED…'
          : (rounded < 30 ? 'INITIALISING' : rounded < 60 ? 'LOADING OPPORTUNITIES' : rounded < 90 ? 'CALIBRATING AI' : 'READY')}
      </div>
    </div>
  );
}

function AppContent() {
  const { loading } = useAuth();
  const [pct, setPct] = useState(0);
  const [stalled, setStalled] = useState(false);
  const [ready, setReady] = useState(false);
  const [forceReady, setForceReady] = useState(false);

  // Runs once on mount. Everything here is anchored to a single wall-clock
  // start time, so there's nothing for a second effect/async call to race
  // against or reset.
  useEffect(() => {
    const startedAt = Date.now();
    let raf;

    function tick() {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min(1, elapsed / AUTH_TIMEOUT_MS);
      const eased = 1 - Math.pow(1 - progress, 2);
      setPct((prev) => Math.max(prev, Math.min(99, eased * 100)));
      if (elapsed < AUTH_TIMEOUT_MS) {
        raf = requestAnimationFrame(tick);
      }
    }
    raf = requestAnimationFrame(tick);

    const stalledTimer = setTimeout(() => setStalled(true), STALLED_AT_MS);
    // Fires no matter what auth/data state is doing at 9s.
    const timeoutTimer = setTimeout(() => setReady(true), AUTH_TIMEOUT_MS);
    // Independent hard ceiling — a last resort if anything above breaks.
    const ceilingTimer = setTimeout(() => setForceReady(true), HARD_CEILING_MS);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(stalledTimer);
      clearTimeout(timeoutTimer);
      clearTimeout(ceilingTimer);
    };
  }, []);

  // Auth resolving early is the happy path — don't make the user wait out
  // the full 9s just because that's the ceiling.
  useEffect(() => {
    if (!loading) setReady(true);
  }, [loading]);

  const showLoader = !ready && !forceReady;

  useEffect(() => {
    if (!showLoader) setPct(100);
  }, [showLoader]);

  if (showLoader) {
    return <Loader pct={pct} stalled={stalled} />;
  }

  return (
    <Suspense fallback={<Loader pct={99} stalled={false} />}>
      <OtpGate />
      <PatchNotesModal />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/faq" element={<FaqPage />} />
        {/* Support needs sign-in (tickets are tracked per customer). Signed-out visitors see a
            "Sign in to contact support" card; people who can't sign in use the form on /login. */}
        <Route path="/support" element={<RequireSupportSignIn><SupportPage /></RequireSupportSignIn>} />
        <Route path="/support/chat" element={<RequireSupportSignIn><SupportChatPage /></RequireSupportSignIn>} />
        <Route path="/support/ticket" element={<RequireSupportSignIn><SupportTicketPage /></RequireSupportSignIn>} />
        <Route path="/support/tickets" element={<RequireSupportSignIn><MyTicketsPage /></RequireSupportSignIn>} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/daily-course" element={<RequireAuth><DailyCoursePage /></RequireAuth>} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/enterprise" element={<EnterprisePage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/report-activity" element={<ReportActivityPage />} />
        <Route path="/onboarding/profile" element={<RequireAuth><ProfileSetup /></RequireAuth>} />
        <Route path="/app" element={<Navigate to="/app/home" replace />} />
        <Route path="/app/:tab" element={<RequireAuth><DashboardPage /></RequireAuth>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary variant="app" message="Elpis hit an unexpected error.">
      <BrowserRouter>
        <AuthProvider>
          <CustomerProvider>
            <AppContent />
          </CustomerProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
